class ProductGalleryModern {
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector('[data-gallery-stage]');
    this.thumbs = root.querySelector('[data-gallery-thumbs]');
    this.slides = Array.from(root.querySelectorAll('.product-gallery-modern__slide[data-media-id]'));
    this.thumbButtons = this.thumbs
      ? Array.from(this.thumbs.querySelectorAll('.product-gallery-modern__thumb[data-media-id]'))
      : [];

    this.variantMediaMap = this.readJson('[data-variant-media-map]');
    this.colorMediaMap = this.readJson('[data-color-media-map]');
    this.colorMediaGroups = this.readJson('[data-color-media-groups]');
    this.currentColor = null;

    this.bindEvents();
    this.applyInitialColorFilter();
  }

  readJson(selector) {
    const node = this.root.querySelector(selector);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (_) {
      return {};
    }
  }

  normalizeMediaId(mediaId) {
    if (!mediaId) return null;
    const value = String(mediaId);
    return value.includes('/') ? value.split('/').pop() : value;
  }

  findSlide(mediaId) {
    const normalized = this.normalizeMediaId(mediaId);
    return this.slides.find((slide) => {
      const slideId = this.normalizeMediaId(slide.dataset.mediaId);
      return slideId === normalized || slide.dataset.mediaId === String(mediaId);
    });
  }

  getColorFromVariant(variant) {
    if (!variant || !Object.keys(this.colorMediaGroups).length) return null;

    const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    for (const option of options) {
      if (Object.prototype.hasOwnProperty.call(this.colorMediaGroups, option)) {
        return option;
      }
    }
    return null;
  }

  filterByColor(colorValue) {
    // Exception : filtrage desactive — on garde TOUTES les photos visibles en permanence.
    // Selectionner une couleur fait juste sauter a sa photo (via showMedia), sans masquer les autres.
    this.currentColor = colorValue || null;

    this.slides.forEach((slide) => {
      slide.classList.remove('is-hidden-by-color');
    });

    this.thumbButtons.forEach((button) => {
      button.classList.remove('is-hidden-by-color');
      button.hidden = false;
    });

    if (this.thumbs) {
      this.thumbs.classList.toggle('is-single-thumb', this.thumbButtons.length <= 1);
    }
  }

  showMedia(mediaId) {
    if (!mediaId) return;
    const slide = this.findSlide(mediaId);
    if (!slide || slide.classList.contains('is-hidden-by-color')) {
      const firstVisible = this.slides.find((item) => !item.classList.contains('is-hidden-by-color'));
      if (firstVisible) {
        this.showMedia(firstVisible.dataset.mediaId);
      }
      return;
    }

    this.slides.forEach((item) => item.classList.remove('is-active'));
    slide.classList.add('is-active');

    this.thumbButtons.forEach((button) => {
      if (button.hidden) return;
      const isActive = button.dataset.mediaId === slide.dataset.mediaId;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  mediaIdFromVariant(variant) {
    if (!variant) return null;

    const mapped = this.variantMediaMap[String(variant.id)];
    if (mapped) return mapped;

    if (variant.featured_media?.id) return variant.featured_media.id;

    if (variant.option1 && this.colorMediaMap[variant.option1]) {
      return this.colorMediaMap[variant.option1];
    }
    if (variant.option2 && this.colorMediaMap[variant.option2]) {
      return this.colorMediaMap[variant.option2];
    }
    if (variant.option3 && this.colorMediaMap[variant.option3]) {
      return this.colorMediaMap[variant.option3];
    }

    return null;
  }

  onVariantChange(variant) {
    const color = this.getColorFromVariant(variant);
    if (color) {
      this.filterByColor(color);
    }
    this.showMedia(
      this.mediaIdFromVariant(variant) ||
        (color && this.colorMediaGroups[color] ? this.colorMediaGroups[color][0] : null)
    );
  }

  applyInitialColorFilter() {
    const sectionId = this.root.id.replace('mediaGallery-', '');
    const variantRoot = document.querySelector(
      `variant-radios[data-section="${sectionId}"], variant-selects[data-section="${sectionId}"]`
    );
    const selectedVariantScript = variantRoot?.querySelector('[data-selected-variant]');

    if (selectedVariantScript) {
      try {
        const variant = JSON.parse(selectedVariantScript.textContent);
        this.onVariantChange(variant);
        return;
      } catch (_) {}
    }

    const firstColor = Object.keys(this.colorMediaGroups)[0];
    if (firstColor) {
      this.filterByColor(firstColor);
      this.showMedia(this.colorMediaGroups[firstColor][0]);
    }
  }

  bindEvents() {
    this.thumbButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.showMedia(button.dataset.mediaId);
      });
    });

    this.root.addEventListener('gallery:variant', (event) => {
      const variant = event.detail?.variant;
      if (variant) {
        this.onVariantChange(variant);
        return;
      }

      const mediaId =
        event.detail?.mediaId ||
        event.detail?.variant?.featured_media?.id ||
        this.mediaIdFromVariant(event.detail?.variant);
      this.showMedia(mediaId);
    });

    const sectionId = this.root.id.replace('mediaGallery-', '');
    const variantRoot = document.querySelector(
      `variant-radios[data-section="${sectionId}"], variant-selects[data-section="${sectionId}"]`
    );

    if (variantRoot) {
      variantRoot.addEventListener('change', () => {
        window.setTimeout(() => {
          const selectedVariantScript = variantRoot.querySelector('[data-selected-variant]');
          if (!selectedVariantScript) return;
          try {
            const variant = JSON.parse(selectedVariantScript.textContent);
            this.onVariantChange(variant);
          } catch (_) {}
        }, 0);
      });

      variantRoot.querySelectorAll('.color-swatches-box input[type="radio"]').forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          const color = input.value;
          if (Object.prototype.hasOwnProperty.call(this.colorMediaGroups, color)) {
            this.filterByColor(color);
            this.showMedia(this.colorMediaGroups[color][0]);
          }
        });
      });
    }
  }
}

function initProductGalleryModern() {
  document.querySelectorAll('[data-gallery-modern]').forEach((gallery) => {
    if (gallery.__galleryModern) return;
    gallery.__galleryModern = new ProductGalleryModern(gallery);
  });
}

document.addEventListener('DOMContentLoaded', initProductGalleryModern);
document.addEventListener('shopify:section:load', initProductGalleryModern);

/* Enhancement Exception : fleches + swipe sur la galerie moderne */
(function () {
  function enhance(root) {
    var inst = root.__galleryModern;
    if (!inst || root.__excSwipe) return;
    root.__excSwipe = true;
    var stage = root.querySelector('[data-gallery-stage]');
    if (!stage || !inst.slides || inst.slides.length < 2) return;

    function visibleSlides() {
      return inst.slides.filter(function (s) {
        return !s.classList.contains('is-hidden-by-color') && getComputedStyle(s).display !== 'none' || s.classList.contains('is-active');
      });
    }
    function allVisible() {
      return inst.slides.filter(function (s) { return !s.classList.contains('is-hidden-by-color'); });
    }
    function go(dir) {
      var vs = allVisible();
      if (vs.length < 2) return;
      var i = vs.findIndex(function (s) { return s.classList.contains('is-active'); });
      if (i < 0) i = 0;
      var n = (i + dir + vs.length) % vs.length;
      inst.showMedia(vs[n].dataset.mediaId);
    }

    function arrow(cls, label, path) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'pgm-arrow ' + cls;
      b.setAttribute('aria-label', label);
      b.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="' + path + '" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      return b;
    }
    var prev = arrow('pgm-arrow--prev', 'Précédent', 'M15 5l-7 7 7 7');
    var next = arrow('pgm-arrow--next', 'Suivant', 'M9 5l7 7-7 7');
    prev.addEventListener('click', function (e) { e.preventDefault(); go(-1); });
    next.addEventListener('click', function (e) { e.preventDefault(); go(1); });
    stage.appendChild(prev);
    stage.appendChild(next);

    /* Swipe tactile (mobile) */
    var x0 = null, y0 = null;
    stage.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { go(dx < 0 ? 1 : -1); }
      x0 = null; y0 = null;
    }, { passive: true });

    /* Drag souris (desktop) */
    var mx0 = null, dragging = false;
    stage.addEventListener('mousedown', function (e) { mx0 = e.clientX; dragging = true; });
    window.addEventListener('mouseup', function (e) {
      if (!dragging) return;
      dragging = false;
      var dx = e.clientX - mx0;
      if (Math.abs(dx) > 50) { go(dx < 0 ? 1 : -1); }
    });
  }
  function enhanceAll() {
    document.querySelectorAll('[data-gallery-modern]').forEach(enhance);
  }
  document.addEventListener('DOMContentLoaded', function () { setTimeout(enhanceAll, 60); });
  document.addEventListener('shopify:section:load', function () { setTimeout(enhanceAll, 60); });
  setTimeout(enhanceAll, 150);
})();
